import { MDXProvider } from "@mdx-js/react";
import { graphql, Link, navigate } from "gatsby";
import Img from "gatsby-image";
import { MDXRenderer } from "gatsby-plugin-mdx";
import { CodePen, Instagram, Spotify, Tweet, Vimeo, YouTube } from "mdx-embed";
import Bookmark from "../components/MDX-components/Bookmark";
import React, { Fragment, useEffect, useState } from "react";
import Gist from "react-gist";
import { InView } from "react-intersection-observer";
import CopyLink from "../components/copy-link";
import CtaPost from "../components/CtaPost";
import Disqus from "../components/disqus";
import PostMeta from "../components/draftbox-meta/PostMeta";
import FbComments from "../components/fb-comments";
import Layout from "../components/Layout";
import Code from "../components/MDX-components/Code";
import HtmlSnippet from "../components/MDX-components/HTMLSnippet";
import Image from "../components/MDX-components/Image";
import ImageGallery from "../components/MDX-components/ImageGallery";
import CustomIframe from "../components/MDX-components/CustomIframe";
import Notice from "../components/MDX-components/Notice";
import NextPrevPost from "../components/NextPrevPosts";
import { DraftboxPost } from "../models/draftbox-post-description.model";
import "../styles/prism.css";
import "../styles/richtext.css";
import EmbedWithCaptionHOC from "../components/MDX-components/EmbedWithCaptionHOC";
import Zoom from "react-medium-image-zoom";

const components = {
  Tweet: EmbedWithCaptionHOC(Tweet),
  CodePen: EmbedWithCaptionHOC(CodePen),
  Instagram: EmbedWithCaptionHOC(Instagram),
  YouTube: EmbedWithCaptionHOC(YouTube),
  Vimeo: EmbedWithCaptionHOC(Vimeo),
  Spotify: EmbedWithCaptionHOC(Spotify),
  HtmlSnippet,
  Notice,
  ImageGallery,
  Image,
  code: Code.Prism,
  pre: Code.Pre,
  Gist: EmbedWithCaptionHOC(Gist),
  Bookmark,
  CustomIframe,
};

type PostTemplateProps = {
  data: {
    prevPost: DraftboxPost;
    nextPost: DraftboxPost;
    draftboxPost: DraftboxPost;
  };
  location: any;
};

const PostTemplate: React.FC<PostTemplateProps> = ({ data, location }) => {
  const { draftboxPost, prevPost, nextPost } = data;

  const [href, sethref] = useState("");

  const [origin, setOrigin] = useState("");

  const [showComments, setshowComments] = useState(false);

  const handleCommentsVisibility = (inView) => {
    if (inView && !showComments) {
      setshowComments(true);
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      sethref(window.location.href);
      setOrigin(window.location.origin);
    }
  }, []);

  const twitterShareUrl = `https://twitter.com/share?text=${draftboxPost.title}&url=${href}`;

  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${href}`;

  const linkedInShareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${href}&title=${draftboxPost.title}`;

  const mailShareUrl = `mailto:?subject=${draftboxPost.title}&body=${href}`;

  let pinterestShareUrl = `https://www.pinterest.com/pin/create/button/?url=${href}&description=${draftboxPost.title}`;
  if (draftboxPost.featureImage?.publicURL) {
    pinterestShareUrl += `&media=${
      origin + draftboxPost.featureImage.publicURL
    }`;
  }

  const whatsAppShareUrl = `https://wa.me/?text=${encodeURIComponent(
    draftboxPost.title + "\n" + href
  )}`;

  const handleNavigation = (e: any, slug) => {
    e.stopPropagation();
    navigate(slug);
  };

  let excerpt = "";

  if (draftboxPost.excerpt) {
    excerpt = draftboxPost.excerpt;
  } else {
    excerpt = "";
  }

  return (
    <Layout>
      <PostMeta post={draftboxPost} amp={false} />
      <div className="spacer my-8 lg:my-12"></div>
      <section className="px-6 max-w-3xl mx-auto">
        <h1
          dangerouslySetInnerHTML={{ __html: draftboxPost.title }}
          className="dangerous-html text-4xl lg:text-5xl font-sansBold break-words leading-tight"
        ></h1>
        {excerpt && (
          <p className="mt-4 mb-6 text-lg lg:text-xl text-gray-700 font-serifNormal">
            {excerpt}
          </p>
        )}
        {excerpt && <hr className="my-2" />}
        <div className="my-4 flex justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex -space-x-4">
              {draftboxPost.authors.map((author, index) => {
                return (
                  <Fragment key={index}>
                    {author.profileImage ? (
                      <>
                        {!author.profileImage.childImageSharp && (
                          <img
                            height="40"
                            width="40"
                            key={index}
                            className="h-10 w-10 rounded-full object-cover"
                            src={author.profileImage?.publicURL}
                            alt=""
                          />
                        )}
                        {author.profileImage.childImageSharp && (
                          <Img
                            className="h-10 w-10 rounded-full object-cover"
                            fixed={author.profileImage.childImageSharp.fixed}
                          ></Img>
                        )}
                      </>
                    ) : (
                      <div className="w-10 h-10 bg-gray-500 border text-white rounded-full flex justify-center items-center mx-auto">
                        <i className="far fa-user"></i>
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </div>
            <div className="flex flex-col">
              <div className="flex">
                <>
                  {draftboxPost.authors?.length < 3 &&
                    draftboxPost.authors.map((author, index) => {
                      return (
                        <a
                          key={index}
                          onClick={(e) =>
                            handleNavigation(e, `/author/${author.slug}`)
                          }
                          className="no-underline hover:underline text-sm text-gray-700 font-sansMedium mr-1 cursor-pointer"
                        >
                          <>
                            {author.name}
                            {index !== draftboxPost.authors.length - 1
                              ? ","
                              : ""}
                          </>
                        </a>
                      );
                    })}
                </>
              </div>
              <div className="flex">
                <span className="no-underline text-xs text-gray-700 mr-1">
                  {draftboxPost.publishedAt} • {draftboxPost.readingTimeStr}
                </span>
              </div>
            </div>
          </div>
          {/* <span className="text-gray-700 text-sm">{post.publishedAt}</span> */}
        </div>
      </section>

      <div className="spacer my-8 lg:my-12"></div>

      {draftboxPost.featureImage?.childImageSharp && (
        <Zoom wrapStyle={{ width: "100%" }}>
          <section className="mx-auto w-full max-w-4xl">
            <Img
              className="w-full"
              style={{ maxHeight: "100%", maxWidth: "100%" }}
              fluid={draftboxPost.featureImage.childImageSharp.fluid as any}
              alt=""
            />
          </section>
        </Zoom>
      )}

      {draftboxPost.featureImage &&
        !draftboxPost.featureImage?.childImageSharp && (
          <Zoom wrapStyle={{ width: "100%" }}>
            <section className="mx-auto w-full max-w-4xl">
              <img
                style={{ maxHeight: "100%" }}
                className="mx-auto"
                src={draftboxPost.featureImage?.publicURL}
                alt={draftboxPost.title}
              />
            </section>
          </Zoom>
        )}

      {draftboxPost.featureImage && (
        <div className="spacer my-8 lg:my-12"></div>
      )}

      <div className="prose prose-lg lg:prose-xl max-w-3xl px-6 mx-auto font-serifNormal text-gray-900">
        <MDXProvider components={components}>
          <MDXRenderer>{draftboxPost.markdownBody}</MDXRenderer>
        </MDXProvider>
      </div>
      <CtaPost />

      {draftboxPost.tags && draftboxPost.tags.length > 0 && (
        <>
          <div className="spacer my-8 lg:my-12"></div>
          <div className="flex items-center max-w-3xl mx-auto flex-wrap px-6">
            {draftboxPost.tags.map((tag, index) => {
              return (
                <div
                  onClick={(e) => handleNavigation(e, `/tag/${tag.slug}`)}
                  className="px-3 py-1 rounded mr-3 text-gray-700 cursor-pointer hover:text-white hover:bg-primary bg-gray-300 mb-4"
                  key={index}
                >
                  {tag.name}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="flex items-center max-w-3xl mt-8 mx-auto px-6">
        <span className="mr-2 text-base text-gray-700">Share:</span>
        <div className="social-icons">
          <ul className="flex">
            <li>
              <a
                className="flex justify-center items-center w-8 h-8 bg-gray-700 hover:bg-primary rounded-full mr-2"
                href={facebookShareUrl}
                aria-label="share on Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-facebook-f fa-fw text-white"></i>
              </a>
            </li>
            <li>
              <a
                className="flex justify-center items-center w-8 h-8 p-2 bg-gray-700 hover:bg-primary rounded-full mr-2"
                href={twitterShareUrl}
                aria-label="share on Twitter"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-twitter fa-fw text-white"></i>
              </a>
            </li>
            <li>
              <a
                className="flex justify-center items-center w-8 h-8 p-2 bg-gray-700 hover:bg-primary rounded-full mr-2"
                href={linkedInShareUrl}
                aria-label="share on Linkedin"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-linkedin-in fa-fw text-white"></i>
              </a>
            </li>
            <li>
              <a
                className="flex justify-center items-center w-8 h-8 bg-gray-700 hover:bg-primary rounded-full mr-2"
                href={pinterestShareUrl}
                aria-label="share on Pinterest"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-pinterest-p fa-fw text-white"></i>
              </a>
            </li>
            <li>
              <a
                className="flex justify-center items-center w-8 h-8 bg-gray-700 hover:bg-primary rounded-full mr-2"
                href={whatsAppShareUrl}
                aria-label="share on Whatsapp"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-whatsapp fa-fw text-white"></i>
              </a>
            </li>
            <li>
              <a
                className="flex justify-center items-center w-8 h-8 bg-gray-700 hover:bg-primary rounded-full mr-2"
                href={mailShareUrl}
                aria-label="share on Mail"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fas fa-envelope fa-fw text-white"></i>
              </a>
            </li>
            <li>
              <CopyLink textToCopy={href} />
            </li>
          </ul>
        </div>
      </div>
      <NextPrevPost prevPost={prevPost} nextPost={nextPost} />
      {/* <InView
        as="div"
        onChange={(inView) => handleCommentsVisibility(inView)}
      ></InView> */}

      {/* Show Comments Button */}
      {!showComments &&
        (process.env.GATSBY_DISQUS_SHORTNAME ||
          process.env.GATSBY_FB_APP_ID) && (
          <div className="flex justify-center mt-8 lg:mt-12">
            <button
              onClick={(e) => setshowComments(true)}
              className="mx-auto block px-3 py-2 text-primary border border-primary rounded hover:text-white hover:bg-primary flex items-center"
            >
              <i className="far fa-comment mr-2"></i>Load Comments
            </button>
          </div>
        )}
      <div>
        {process.env.GATSBY_DISQUS_SHORTNAME && showComments && (
          <>
            {/* <hr className="spacer my-8 container mx-auto" /> */}
            <section className="max-w-4xl container mx-auto px-6 mt-16">
              <Disqus slug={draftboxPost.slug} title={draftboxPost.title} />
            </section>
          </>
        )}
        {process.env.GATSBY_FB_APP_ID && showComments && (
          <>
            {/* <hr className="spacer my-8 container mx-auto" /> */}
            <section className="max-w-4xl container mx-auto px-6 mt-16">
              <FbComments href={href} />
            </section>
          </>
        )}
      </div>
      <div className="spacer my-8 lg:my-12"></div>
      {/* <CtaMini /> */}
    </Layout>
  );
};

export const postDataQuery = graphql`
  query($postSeoSlug: String!, $prev: String, $next: String) {
    draftboxPost(postSeoSlug: { eq: $postSeoSlug }) {
      ...DraftboxPostDetails
    }
    prevPost: draftboxPost(postSeoSlug: { eq: $prev }) {
      title
      excerpt
      excerpt
      customExcerpt
      postSeoSlug
      updatedAt(formatString: "DD MMMM YYYY")
      publishedAt(formatString: "DD MMMM YYYY")
    }
    nextPost: draftboxPost(postSeoSlug: { eq: $next }) {
      title
      excerpt
      customExcerpt
      postSeoSlug
      updatedAt(formatString: "DD MMMM YYYY")
      publishedAt(formatString: "DD MMMM YYYY")
    }
  }
`;

export default PostTemplate;
